import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { TableUtility } from '@utility/dynamic-table';
import { toast } from 'react-toastify';
import { MODULE_NAMES } from '../../../Constants/modules';
import { AuthContext } from '../../../Contexts/AuthContext';
import FormDialog from '../../../Components/Modal/modal';
import UseAxios from '../../../Api/http/http';
import {
  getIssueCategory,
  createIssueCategory,
  updateIssueCategory,
} from '../../../Api/request/issueCategory';
import ControlledSwitches from '../../../Components/Switch/switch';
import EditActionIcon from '../../../Assets/images/svg/dar_edit.svg';
import alertIcon from '../../../Assets/images/svg/alert_icon.svg';
import { issueCategoryTableConfig } from './issueCategoryTableConfig';
import './issueCategory.scss';

const EMPTY_FORM = { name: '' };
const EMPTY_PERMISSIONS = [];

const getApiErrorMessage = (response) => {
  const payload = response?.data;
  if (payload instanceof Error) {
    return payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || payload.message
      || 'Something went wrong.';
  }
  if (payload?.status === false) {
    return payload?.message
      || payload?.response?.data?.message
      || payload?.response?.data?.externalMessage
      || 'Something went wrong.';
  }
  return '';
};

const normalizeIssueCategoryLists = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : response?.data?.data || response?.data?.issueCategories || [];

  return rows.map((issueCategory) => normalizeIssueCategory(issueCategory));
};

const normalizeIssueCategory = (issueCategory) => ({
  id: issueCategory.issueCategoryId,
  name: issueCategory.issueCategoryName || '',
  isActive: Boolean(issueCategory.isActive),
});

const IssueCategoryForm = ({ mode, form, error, isSubmitDisabled, onChange, onClose, onSubmit }) => createPortal(
  <div className="issue-category-modal-backdrop">
    <form className="issue-category-modal" onSubmit={onSubmit}>
      <div className="issue-category-modal__header">
        {mode === 'edit' ? 'Edit Issue Category' : 'Add Issue Category'}
      </div>
      <div className="issue-category-modal__body">
        <label htmlFor="department-name" className={error ? 'is-error' : ''}>
           Issue Category<span className="required-asterisk">*</span>
        </label>
        <input
          id="department-name"
          name="name"
          type="text"
          placeholder="Enter Issue Category"
          value={form.name}
          onChange={onChange}
          className={error ? 'is-error' : ''}
          autoComplete="off"
        />
      </div>
      <div className="issue-category-modal__footer">
        <button type="button" className="issue-category-modal__btn issue-category-modal__btn--ghost"
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className="issue-category-modal__btn issue-category-modal__btn--primary"
          disabled={isSubmitDisabled}
        >
          {mode === 'edit' ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  </div>,
  document.body,
);

const IssueCategory = () => {
  const { userProfile } = React.useContext(AuthContext);
  const permission = userProfile?.permissions?.[MODULE_NAMES.CONFIGURATION_ISSUE_CATEGORY];
  const isSuperAdmin = userProfile?.roleDetails?.some(
    (role) => role.roleName === 'Super Admin',
  );
  const normalizedPermissions = useMemo(
    () => (permission || EMPTY_PERMISSIONS).map((action) => String(action).toLowerCase()),
    [permission],
  );
  const hasAccess = useCallback((action) => {
    if (isSuperAdmin) return true;
    const actionAliases = {
      read: ['read', 'view'],
      create: ['create', 'add'],
      update: ['update', 'edit'],
    };
    return actionAliases[action]?.some((alias) => normalizedPermissions.includes(alias)) || false;
  }, [isSuperAdmin, normalizedPermissions]);
  const [ issueCategories, setIssueCategories ] = useState([]);
  const [ isLoading, setIsLoading ] = useState(false);
  const [dialog, setDialog] = useState({ isOpen: false, mode: 'add', id: null });
  const [toggleIssueCategory, setToggleIssueCategory] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hasError, setHasError] = useState(false);

  const loadIssueCategoryList = useCallback(async () => {
    if (!hasAccess('read')) return;
    const response = await UseAxios(getIssueCategory(), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    setIssueCategories(normalizeIssueCategoryLists(response));
  }, [hasAccess]);

  useEffect(() => {
    loadIssueCategoryList();
  }, [loadIssueCategoryList]);

  const closeForm = useCallback(() => {
    setDialog({ isOpen: false, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, []);

  const openAdd = useCallback(() => {
    if (!hasAccess('create')) return;
    setDialog({ isOpen: true, mode: 'add', id: null });
    setForm(EMPTY_FORM);
    setHasError(false);
  }, [hasAccess]);

  const openEdit = useCallback((provider) => {
    if (!hasAccess('update')) return;
    setDialog({ isOpen: true, mode: 'edit', id: provider.id });
    setForm({ name: provider.name });
    setHasError(false);
  }, [hasAccess]);

  const selectedIssueCategory = issueCategories.find((provider) => provider.id === dialog.id);
  const isSubmitDisabled = !form.name.trim()
    || (dialog.mode === 'edit' && (form.name.trim() === selectedIssueCategory?.name.trim()
    ));

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!hasAccess(dialog.mode === 'edit' ? 'update' : 'create')) return;
    const name = form.name.trim();
    if (!name) {
      setHasError(true);
      toast.error('Please enter a issue category name.');
      return;
    }
    const response = dialog.mode === 'edit'
      ? await UseAxios(
        updateIssueCategory(dialog.id, name, selectedIssueCategory?.isActive ?? true),
        setIsLoading,
      )
      : await UseAxios(createIssueCategory(name), setIsLoading);
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }
    toast.success(dialog.mode === 'edit'
      ? 'Issue Category updated successfully.'
      : 'Issue Category added successfully.');
    const savedIssueCategory = normalizeIssueCategory(response?.data?.issueCategories || response?.data);
    setIssueCategories((currentIssueCategories) => (
      dialog.mode === 'edit'
        ? currentIssueCategories.map((iCategory) => (
          iCategory.id === savedIssueCategory.id ? savedIssueCategory : iCategory
        ))
        : [...currentIssueCategories, savedIssueCategory]
    ));
    closeForm();
  }, [closeForm, dialog.id, dialog.mode, form.name, hasAccess, selectedIssueCategory]);

  const handleToggle = useCallback((iCategory) => {
    if (!hasAccess('update')) return;
    setToggleIssueCategory(iCategory);
  }, [hasAccess]);

  const handleCloseToggleDialog = useCallback(() => {
    setToggleIssueCategory(null);
  }, []);

  const handleConfirmToggle = useCallback(async () => {
    if (!toggleIssueCategory || !hasAccess('update')) return;

    const nextStatus = !toggleIssueCategory.isActive;
    const response = await UseAxios(
      updateIssueCategory(
        toggleIssueCategory.id, toggleIssueCategory.name, nextStatus),
      setIsLoading,
    );
    const errorMessage = getApiErrorMessage(response);
    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    setIssueCategories((currentIssueCategories) => currentIssueCategories.map((issueCategory) => (
      issueCategory.id === toggleIssueCategory.id
        ? { ...issueCategory, isActive: nextStatus } : issueCategory
    )));
    toast.success(`${toggleIssueCategory.name} is now ${nextStatus ? 'active' : 'inactive'}.`);
    handleCloseToggleDialog();
  }, [handleCloseToggleDialog, hasAccess, toggleIssueCategory]);

  const tableData = useMemo(() => issueCategories.map((issueCategoryData, index) => ({
    ...issueCategoryData,
    slNo: index + 1,
    issueCategory: issueCategoryData.name,
    status: issueCategoryData.isActive ? 'Active' : 'Inactive',
    action: (
      <div className="issue-category-list-actions" onClick={(event) => event.stopPropagation()}>
        <ControlledSwitches
          checked={issueCategoryData.isActive}
          disabled={!hasAccess('update')}
          switchCss="switchCss"
          handleChange={() => handleToggle(issueCategoryData)}
        />
        <button
          type="button"
          className="issue-category-list-action-btn"
          title="Edit Issue Category"
          aria-label={`Edit ${issueCategoryData.issueCategory}`}
          disabled={!hasAccess('update')}
          onClick={() => openEdit(issueCategoryData)}
        >
          <img src={EditActionIcon} alt="" />
        </button>
      </div>
    ),
  })), [handleToggle, hasAccess, openEdit, issueCategories]);

  const configuration = useMemo(() => ({
    generalSettings: {
      enableApiFiltering: false,
      showPagination: false,
      showToolBar: true,
      userInfo: {},
      handlers: { handleAddIconClick: openAdd },
      toolBarSettings: {
        showAddNew: hasAccess('create'),
        showFilterOption: false,
        showDownloadExcel: false,
        showDownloadCsv: false,
        showExpandCollapse: false,
        showColumnCustomize: false,
        showHelp: false,
        showSearch: false,
        showSaveView: false,
        showReset: false,
        showToolBarHeading: true,
        toolBarHeading: 'Issue Categories',
      },
    },
    showToolBar: true,
    tableSettings: {
      size: 'small',
      enableTreeTable: false,
      showExpandCollapse: false,
      tableConfig: issueCategoryTableConfig,
      loaderSkeleton: { rows: 8, bodyCells: 1, columns: issueCategoryTableConfig.length },
      paginationConfig: { page: 1, limit: tableData.length, total: tableData.length, totalPages: 1 },
      tableScrollHeight: 'calc(100vh - 140px)',
      maxFrozenColumns: { desktop: 1, tablet: 1, mobile: 1 },
    },
  }), [hasAccess, openAdd, tableData.length]);

  if (!hasAccess('read')) {
    return <div style={{marginTop: '30px'}}>You do not have permission to view issue categories.</div>;
  }

  const onChangeCategoryForm = (event) => {
    const { name, type, checked, value } = event.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setHasError(false);
  };

  return (
    <div className="issue-category-list-tu">
      <TableUtility configuration={configuration} data={tableData} loading={isLoading} />
      {dialog.isOpen && (
        <IssueCategoryForm
          mode={dialog.mode}
          form={form}
          error={hasError}
          isSubmitDisabled={isSubmitDisabled}
          onChange={onChangeCategoryForm}
          onClose={closeForm}
          onSubmit={handleSubmit}
        />
      )}
      <FormDialog
        open={Boolean(toggleIssueCategory)}
        title={`${toggleIssueCategory?.isActive ? 'Disable' : 'Enable'} Issue Category`}
        dialogCss="dialog-message"
        dialogPaperCss="client-enable-dialog-paper"
        paperprops={{}}
        dialogBodycontent={(
          <div className="dialog-body-text">
            <div className="dialog-alert-icon">
              <img alt="Alert Icon" className="icon-image" src={alertIcon} />
            </div>
            <div className="dialog-message-text">
              {`Are you sure you want to ${toggleIssueCategory?.isActive
                ? 'disable' : 'enable'} this issue category?`}
            </div>
          </div>
        )}
        button1="Cancel"
        cancelButtonId="cancel-cloud-provider-toggle"
        button2={toggleIssueCategory?.isActive ? 'Disable' : 'Enable'}
        submitButtonId="confirm-cloud-provider-toggle"
        button1Action={handleCloseToggleDialog}
        button2Action={handleConfirmToggle}
      />
    </div>
  );
};

export default IssueCategory;
